import { ProductPageTemplate } from "@/components/ProductPageTemplate";
import { Shield, Users, FileText, AlertTriangle, Scale, Briefcase } from "lucide-react";
import { getImageUrl } from '@/lib/utils';

const LiabilityCoverage = () => {
  const pageData = {
    // SEO Meta Data
    title: "Liability Insurance UAE | Public & Professional Liability | Yalla Compare",
    metaDescription: "Comprehensive liability insurance for UAE businesses. Get public liability, professional indemnity, and product liability coverage. Protect against claims and lawsuits.",

    // Hero Section
    heroTitle: "Complete Liability Protection for UAE Businesses",
    heroSubtitle: "Liability Coverage",
    heroDescription: "Shield your business from costly claims and lawsuits with comprehensive liability insurance. From public liability to professional indemnity, we provide complete protection.",
    heroImage: getImageUrl("lia-icon.png"),

    // Key Benefits
    keyBenefits: [
      "Protection against third-party claims and lawsuits",
      "Coverage for legal defense costs and settlements",
      "Professional indemnity for service-based businesses",
      "Product liability for manufacturers and retailers",
      "Flexible coverage limits up to AED 50 million",
      "UAE legal compliance and regulatory protection"
    ],

    // Features Section
    features: [
      {
        title: "Public Liability",
        description: "Protection against claims from third parties for bodily injury or property damage caused by your business operations.",
        icon: <Users className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Professional Indemnity",
        description: "Coverage for claims arising from professional advice, services, or errors and omissions in your work.",
        icon: <Briefcase className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Product Liability",
        description: "Protection against claims related to products you manufacture, supply, or sell that cause injury or damage.",
        icon: <Shield className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Legal Defense",
        description: "Comprehensive legal support including defense costs, court fees, and expert witness expenses.",
        icon: <Scale className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Regulatory Compliance",
        description: "Meet UAE legal requirements and protect against regulatory fines and penalties.",
        icon: <FileText className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Risk Management",
        description: "Expert guidance on reducing liability exposure and implementing best practices for your industry.",
        icon: <AlertTriangle className="h-6 w-6 text-blue-600" />
      }
    ],

    // Coverage Details
    coverageDetails: [
      {
        title: "Public Liability Insurance",
        description: "Essential protection for businesses that interact with the public",
        included: [
          "Third-party bodily injury claims",
          "Property damage to third parties",
          "Legal defense and court costs",
          "Medical expenses for injured parties",
          "Loss of use claims",
          "Advertising injury protection",
          "Premises liability coverage",
          "Product recall expenses"
        ]
      },
      {
        title: "Professional Liability Coverage",
        description: "Specialized protection for professional service providers",
        included: [
          "Errors and omissions coverage",
          "Professional negligence claims",
          "Breach of duty allegations",
          "Failure to deliver services",
          "Intellectual property infringement",
          "Data breach liability",
          "Regulatory investigation costs",
          "Loss of documents coverage"
        ]
      }
    ],

    // Why Choose Us
    whyChooseUs: [
      {
        title: "Industry Expertise",
        description: "Specialized knowledge of liability risks across different industries in the UAE market."
      },
      {
        title: "Comprehensive Coverage",
        description: "Multiple liability types under one policy with competitive premiums and broad protection."
      },
      {
        title: "Claims Excellence",
        description: "Experienced claims handling team with proven track record in liability claim management."
      },
      {
        title: "Legal Network",
        description: "Access to top legal professionals and experts for defense and settlement negotiations."
      }
    ],

    // FAQ Section
    faqs: [
      {
        question: "What's the difference between public and professional liability insurance?",
        answer: "Public liability covers claims from third parties for injury or property damage, while professional liability covers claims arising from professional services, advice, or errors in your work."
      },
      {
        question: "How much liability coverage do I need for my UAE business?",
        answer: "Coverage amounts depend on your industry, business size, and risk exposure. We typically recommend minimum AED 1 million for small businesses, with higher limits for larger operations."
      },
      {
        question: "Are legal defense costs covered in addition to the policy limit?",
        answer: "This varies by policy. Some insurers include defense costs within the policy limit, while others provide them in addition. We'll help you understand your specific coverage."
      },
      {
        question: "Does liability insurance cover intentional acts or criminal behavior?",
        answer: "No, liability insurance does not cover intentional wrongdoing, criminal acts, or deliberate violations of law. Coverage is for accidental incidents and negligent acts."
      }
    ],

    // CTA Section
    ctaTitle: "Secure Your Business with Liability Insurance",
    ctaDescription: "Don't let unexpected claims threaten your business. Get comprehensive liability coverage tailored to your UAE operations.",
    primaryCta: "Get Liability Insurance Quote",
    secondaryCta: "Explore All Coverage Options",

    // Product ID for quote form
    productId: "third-party-liability"
  };

  return <ProductPageTemplate {...pageData} />;
};

export default LiabilityCoverage;
